import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { FaPlus } from 'react-icons/fa';
import { useNotificationModal } from '../../hooks/useNotificationModal';
import CountryCodeSelect from '../../components/CountryCodeSelect';
import CountryDropdown from "../../components/CountryDropdown";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function RegisterCompany() {
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    company_code: "",
    company_name: "",
    primary_category_ids: [], // Can have multiple categories
    address_line_1: "",
    address_line_2: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    company_logo: null,
    currency: "LKR",
    company_country_code: "+94",
    company_phone: "",
    company_email: "",
    roles: [],
    // Feature toggles for variants (default to enabled)
    enable_colors: true,
    enable_sizes: true,
    enable_fits: true,
    enable_materials: true,
  });

  const [currentRole, setCurrentRole] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(isEditMode);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const { showError, showSuccess } = useNotificationModal();
  const navigate = useNavigate();

  // Override console.error to display errors in modal
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      originalConsoleError(...args);
      const errorMessage = args
        .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
        .join(' ');
      showError(errorMessage);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, [showError]);

  // Company autocomplete states
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const companyInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Currency options from backend API
  const [currencies, setCurrencies] = useState([
    { value: "LKR", label: "LKR (₨) - Sri Lankan Rupee" },
  ]);

  const [primaryCategories, setPrimaryCategories] = useState([]);

  // Fetch available companies
  useEffect(() => {
    async function fetchAvailableCompanies() {
      setIsLoadingCompanies(true);
      try {
        const response = await fetch(`${BASE_URL}/api/admin/available-companies`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch companies: ${response.status}`);
        }
        const data = await response.json();
        
        // Extract the companies array - handle multiple response formats
        let companies = [];
        if (data && data.data && Array.isArray(data.data)) {
          companies = data.data;
        } else if (Array.isArray(data)) {
          companies = data;
        } else if (data && Array.isArray(data.companies)) {
          companies = data.companies;
        }
        
        if (companies.length > 0) {
          setAvailableCompanies(companies);
        } else {
          console.warn("No companies found in response");
          showError("No companies found in response");
          setAvailableCompanies([]);
        }
      } catch (error) {
        console.error("Error fetching available companies:", error);
        setAvailableCompanies([]);
      } finally {
        setIsLoadingCompanies(false);
      }
    }
    fetchAvailableCompanies();
  }, [showError]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        companyInputRef.current &&
        !companyInputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    async function fetchCurrencies() {
      try {
        const res = await fetch(
          `${BASE_URL}/api/admin/currencies/get-all-currency-symbols`
        );
        if (!res.ok) throw new Error("Currency API error");
        const data = await res.json();
        if (data && data.currencies) {
          setCurrencies(data.currencies);
        } else {
          throw new Error("Currency API returned no symbols");
        }
      } catch (err) {
        setCurrencies([
          { value: "LKR", label: "LKR (₨) - Sri Lankan Rupee" },
          { value: "GBP", label: "GBP (£) - British Pound Sterling" },
          { value: "USD", label: "USD ($) - US Dollar" },
          { value: "EUR", label: "EUR (€) - Euro" },
          { value: "AED", label: "AED (د.إ) - UAE Dirham" },
        ]);
      }
    }
    fetchCurrencies();

    async function fetchPrimaryCategories() {
      try {
        const token = localStorage.getItem("authToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const res = await fetch(`${BASE_URL}/api/admin/categories/get-primary-categories`, {
          headers: headers,
        });
        if (!res.ok) throw new Error("Category API error");
        const data = await res.json();
        
        let categories = [];
        if (Array.isArray(data)) {
          // Data is directly an array
          categories = data;
        } else if (data && data.categories && Array.isArray(data.categories)) {
          // Data is wrapped with categories field
          categories = data.categories;
        }
        
        if (categories.length > 0) {
          const options = categories.map(cat => ({
            value: cat.primary_category_id,
            label: cat.primary_category_name
          }));
          setPrimaryCategories(options);
        } else {
          console.log("No categories found in response");
        }
      } catch (err) {
        console.error("Error fetching primary categories:", err);
      }
    }
    fetchPrimaryCategories();
  }, []);

  const fetchCompanyData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const response = await fetch(`${BASE_URL}/api/admin/companies/${id}`, {
        headers: getAuthHeaders(),
      });

      // Check if response is HTML (error page) before trying to parse JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const errorText = await response.text();
        console.error("Non-JSON response received:", errorText);
        showError(
          `Server returned non-JSON response. Status: ${response.status}`,
          'Error'
        );
        return;
      }

      const data = await response.json();

      if (response.ok && data.success) {
        const company = data.company;

        // Ensure country code is properly formatted with + prefix
        // Convert to string first to handle both string and number types
        let countryCode = "+94"; // Default
        if (company.company_country_code !== null && company.company_country_code !== undefined && company.company_country_code !== "") {
          const codeStr = String(company.company_country_code).trim();
          countryCode = codeStr.startsWith('+') ? codeStr : `+${codeStr}`;
        }

        // Convert primary_categories array to array of IDs
        const primaryCategoryIds = company.primary_categories && company.primary_categories.length > 0
          ? company.primary_categories.map(cat => cat.primary_category_id)
          : [];

        setFormData({
          company_code: company.company_code || "",
          company_name: company.company_name || "",
          primary_category_ids: primaryCategoryIds,
          address_line_1: company.address_line1 || "",
          address_line_2: company.address_line2 || "",
          city: company.city || "",
          state: company.state || "",
          country: company.country || "",
          postal_code: company.postal_code || "",
          company_logo: null, // Reset file input
          currency: company.currency || "LKR",
          company_country_code: countryCode,
          company_phone: company.company_phone || "",
          company_email: company.company_email || "",
          roles: company.roles && company.roles.length > 0 ? company.roles : [],
          enable_colors: company.enable_colors === undefined ? true : !!company.enable_colors,
          enable_sizes: company.enable_sizes === undefined ? true : !!company.enable_sizes,
          enable_fits: company.enable_fits === undefined ? true : !!company.enable_fits,
          enable_materials: company.enable_materials === undefined ? true : !!company.enable_materials,
        });

        if (company.company_logo) {
          setCurrentLogo(company.company_logo);
        }
      } else {
        const errorMessage = data.message || "Company Data Not Found";
        showError(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
      showError(error.message || "Error! Please try again.");
    } finally {
      setIsLoadingData(false);
    }
  }, [id, showError]);

  useEffect(() => {
    if (isEditMode && id) {
      // Check if user is authenticated
      const token = localStorage.getItem("authToken");
      if (!token) {
        showError("Authentication required. Please log in.");
        setIsLoadingData(false);
        return;
      }
      fetchCompanyData();
    }
  }, [id, isEditMode, fetchCompanyData, showError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Handle company name autocomplete
    if (name === "company_name") {
      if (value.trim() === "") {
        setFilteredCompanies([]);
        setShowSuggestions(false);
      } else {
        const searchTerm = value.toLowerCase().trim();
        
        const filtered = availableCompanies.filter((company) => {
          // API uses companyName property (camelCase)
          const companyName = String(
            company.companyName || 
            company.company_name || 
            company.name || 
            ""
          ).toLowerCase().trim();
          
          return companyName.includes(searchTerm);
        });
        
        setFilteredCompanies(filtered);
        setShowSuggestions(filtered.length > 0);
      }
    }
  };

  const selectCompanyFromSuggestion = (company) => {
    const companyName = 
      company.companyName || 
      company.company_name || 
      company.name || 
      "";
    
    const companyCode = 
      company.code || 
      company.company_code || 
      company.companyCode || 
      "";
    
    // Auto-fill other fields from the selected company data
    setFormData((prev) => ({
      ...prev,
      company_code: companyCode,
      company_name: companyName,
      address_line_1: company.address_line1 || "",
      address_line_2: company.address_line2 || "",
      city: company.city || "",
      state: company.state || company.state || "",
      country: company.countryName || company.country || "",
      postal_code: company.postalCode || company.postal_code || "",
      company_email: company.email || "",
      company_phone: (company.phone || company.company_phone || company.companyPhone || "").replace(/\D/g, ""),
      currency: company.currency || prev.currency,
      company_country_code: company.countryCode || company.country_code || company.companyCountryCode || prev.company_country_code,
    }));
    setShowSuggestions(false);
    setFilteredCompanies([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, company_logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addRole = () => {
    if (currentRole.trim() !== "") {
      setFormData((prev) => ({ ...prev, roles: [...prev.roles, currentRole.trim()] }));
      setCurrentRole("");
    }
  };

  const removeRole = (index) => {
    const newRoles = formData.roles.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, roles: newRoles }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Check if user is authenticated
      const token = localStorage.getItem("authToken");
      if (!token) {
        showError("Authentication required. Please log in.");
        setIsLoading(false);
        return;
      }

      // Validate country code format
      const cleanCountryCode = String(formData.company_country_code).replace(/\D/g, '');
      if (!formData.company_country_code || cleanCountryCode.length < 1 || cleanCountryCode.length > 4) {
        showError('Country code must be 1-4 digits (e.g., +94, +1, +971)', 'Error');
        setIsLoading(false);
        return;
      }

      // Ensure country code has + prefix before saving
      const countryCodeToSave = formData.company_country_code
        ? (String(formData.company_country_code).startsWith('+')
          ? formData.company_country_code
          : `+${formData.company_country_code}`)
        : "+94";

      const companyFormData = new FormData();
      companyFormData.append("company_code", formData.company_code);
      companyFormData.append("company_name", formData.company_name);
      companyFormData.append("address_line_1", formData.address_line_1);
      companyFormData.append("address_line_2", formData.address_line_2);
      companyFormData.append("city", formData.city);
      companyFormData.append("state", formData.state);
      companyFormData.append("country", formData.country);
      companyFormData.append("postal_code", formData.postal_code);
      companyFormData.append("currency", formData.currency);
      companyFormData.append("company_country_code", countryCodeToSave);
      companyFormData.append("company_phone", formData.company_phone);
      companyFormData.append("company_email", formData.company_email);
      // Send multiple primary category IDs as JSON array
      companyFormData.append("primary_category_ids", JSON.stringify(formData.primary_category_ids || []));

      // Filter out empty roles and append them
      const validRoles = formData.roles.filter(role => role.trim() !== "");
      companyFormData.append("roles", JSON.stringify(validRoles));
      // Append feature toggles (send as 1/0)
      companyFormData.append('enable_colors', formData.enable_colors ? '1' : '0');
      companyFormData.append('enable_sizes', formData.enable_sizes ? '1' : '0');
      companyFormData.append('enable_fits', formData.enable_fits ? '1' : '0');
      companyFormData.append('enable_materials', formData.enable_materials ? '1' : '0');

      if (formData.company_logo) {
        companyFormData.append("company_logo", formData.company_logo);
      }

      // Get auth headers
      const authHeaders = getAuthHeaders();

      let response;
      if (isEditMode) {
        // Update existing company using CompanyController
        response = await fetch(`${BASE_URL}/api/admin/companies/${id}`, {
          method: "PUT",
          headers: {
            ...authHeaders,
          },
          body: companyFormData,
        });
      } else {
        // Create new company using CompanyController
        response = await fetch(`${BASE_URL}/api/admin/companies`, {
          method: "POST",
          headers: {
            ...authHeaders,
          },
          body: companyFormData,
        });
      }

      // Check if response is HTML (error page) before trying to parse JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const errorText = await response.text();
        console.error("Non-JSON response received:", errorText);
        throw new Error(
          `Server returned non-JSON response. Status: ${response.status}`
        );
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.message ||
          `Failed to ${isEditMode ? "update" : "create"} company`
        );
      }

      if (isEditMode) {
        showSuccess(
          `Company "${formData.company_name}" updated successfully!`,
          'Success'
        );
      } else {
        showSuccess(
          `Company "${formData.company_name}" created successfully! Company Code: ${formData.company_code}`,
          'Success'
        );

        // Reset form for create mode
        setFormData({
          company_code: "",
          company_name: "",
          primary_category_ids: [],
          address_line_1: "",
          address_line_2: "",
          city: "",
          state: "",
          country: "",
          postal_code: "",
          company_logo: null,
          currency: "LKR",
          company_country_code: "+94",
          company_phone: "",
          company_email: "",
          roles: [],
          enable_colors: true,
          enable_sizes: true,
          enable_fits: true,
          enable_materials: true,
        });
        setCurrentRole("");
        setLogoPreview(null);
        setCurrentLogo(null);
      }

      // Redirect after success
      setTimeout(() => {
        navigate("/vcm-admin/view-companies");
      }, 2000);
    } catch (error) {
      console.error(
        `Company ${isEditMode ? "update" : "creation"} error:`,
        error
      );
      showError(error.message || "Something went wrong. Please try again.", 'Error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <Container>
        <Card className="register-card">
          <Button
            variant="secondary"
            className="btn-custom-secondary mb-3"
            onClick={() => navigate("/vcm-admin/view-companies")}
          >
            ← Back to Companies
          </Button>

          <Card.Body>
            <h2 className="register-title">
              {isEditMode ? "Edit Company" : "Add New Company"}
            </h2>

            {isLoadingData ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">
                    Loading company data...
                  </span>
                </Spinner>
                <p className="mt-2 text-muted">Loading company data...</p>
              </div>
            ) : (
              <>
                <Form className="register-form" onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company Name</Form.Label>
                        <div
                          style={{ position: "relative" }}
                          ref={companyInputRef}
                        >
                          <Form.Control
                            type="text"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleChange}
                            placeholder="Start typing to see existing companies..."
                            style={{ fontSize: "13px" }}
                            required
                            autoComplete="off"
                            disabled={isEditMode}
                          />
                          {isLoadingCompanies && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                backgroundColor: "white",
                                border: "1px solid #dee2e6",
                                borderRadius: "4px",
                                padding: "10px 15px",
                                marginTop: "4px",
                                fontSize: "13px",
                                color: "#666",
                              }}
                            >
                              Loading companies...
                            </div>
                          )}
                          {!isLoadingCompanies &&
                            formData.company_name &&
                            filteredCompanies.length > 0 && (
                              <div
                                ref={suggestionsRef}
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  backgroundColor: "white",
                                  border: "2px solid #007bff",
                                  borderRadius: "4px",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                  zIndex: 9999,
                                  maxHeight: "300px",
                                  overflowY: "auto",
                                  marginTop: "4px",
                                }}
                              >
                                {filteredCompanies.map((company, index) => {
                                  const displayName =
                                    company.companyName || "Unknown";
                                  return (
                                    <div
                                      key={index}
                                      onClick={() =>
                                        selectCompanyFromSuggestion(company)
                                      }
                                      style={{
                                        padding: "12px 15px",
                                        cursor: "pointer",
                                        borderBottom:
                                          index < filteredCompanies.length - 1
                                            ? "1px solid #f0f0f0"
                                            : "none",
                                        fontSize: "13px",
                                        transition: "background-color 0.2s",
                                        backgroundColor: "white",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "#e7f3ff";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "white";
                                      }}
                                    >
                                      <strong>{displayName}</strong>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company Phone</Form.Label>
                        <div className="d-flex gap-2">
                          <CountryCodeSelect
                            value={formData.company_country_code}
                            onChange={(code) =>
                              setFormData((prev) => ({
                                ...prev,
                                company_country_code: code,
                              }))
                            }
                            placeholder="Code"
                            isRequired={true}
                            width="100px"
                          />
                          <Form.Control
                            type="text"
                            name="company_phone"
                            value={formData.company_phone}
                            onChange={(e) => {
                              // Only allow digits
                              const value = e.target.value.replace(/\D/g, "");
                              setFormData((prev) => ({
                                ...prev,
                                company_phone: value,
                              }));
                            }}
                            placeholder="Enter company phone"
                            required
                            disabled={isEditMode}
                            style={{
                              flex: 1,
                              height: "48px",
                              fontSize: "13px",
                              borderRadius: "12px",
                              backgroundColor: "#f7fafc",
                              border: "2px solid #e2e8f0",
                            }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="company_email"
                          value={formData.company_email}
                          onChange={handleChange}
                          placeholder="Enter company email"
                          style={{ fontSize: "13px" }}
                          required
                          disabled={isEditMode}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Currency</Form.Label>
                        <Select
                          name="currency"
                          value={currencies.find(
                            (c) => c.value === formData.currency,
                          )}
                          onChange={(selected) =>
                            handleChange({
                              target: {
                                name: "currency",
                                value: selected.value,
                              },
                            })
                          }
                          options={currencies}
                          isSearchable={true}
                          placeholder="Select Currency"
                          style={{ fontSize: "13px" }}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Address Line 1</Form.Label>
                        <Form.Control
                          type="text"
                          name="address_line_1"
                          value={formData.address_line_1}
                          onChange={handleChange}
                          placeholder="Enter address line 1"
                          style={{ fontSize: "13px" }}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Address Line 2{" "}
                          <span className="text-muted">(Optional)</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="address_line_2"
                          value={formData.address_line_2}
                          onChange={handleChange}
                          placeholder="Enter address line 2"
                          style={{ fontSize: "13px" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="Enter city"
                          style={{ fontSize: "13px" }}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          State <span className="text-muted">(Optional)</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          placeholder="Enter state"
                          style={{ fontSize: "13px" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <CountryDropdown
                          value={formData.country || ""}
                          onChange={(country) =>
                            setFormData((prev) => ({ ...prev, country: country }))
                          }
                          placeholder="Select country"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Postal Code{" "}
                          <span className="text-muted">(Optional)</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="postal_code"
                          value={formData.postal_code}
                          onChange={handleChange}
                          placeholder="Enter postal code"
                          style={{ fontSize: "13px" }}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company Roles</Form.Label>
                        <div className="d-flex gap-2 mb-2 align-items-center">
                          <Form.Control
                            type="text"
                            value={currentRole}
                            onChange={(e) => setCurrentRole(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addRole();
                              }
                            }}
                            placeholder="Enter role"
                            style={{
                              flex: 1,
                              height: "48px",
                              fontSize: "13px",
                              borderRadius: "12px",
                              backgroundColor: "#f7fafc",
                              border: "2px solid #e2e8f0",
                            }}
                          />
                          <FaPlus
                            variant="success"
                            onClick={addRole}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          />
                        </div>

                        {/* Display added roles as badges */}
                        {formData.roles.length > 0 && (
                          <div
                            className="mt-2"
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "8px",
                            }}
                          >
                            {formData.roles.map((role, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "6px 12px",
                                  backgroundColor: "#e3f2fd",
                                  border: "1px solid #90caf9",
                                  borderRadius: "20px",
                                  fontSize: "0.9rem",
                                  color: "#1976d2",
                                  gap: "8px",
                                }}
                              >
                                <span>{role}</span>
                                <span
                                  onClick={() => removeRole(index)}
                                  style={{
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    color: "#d32f2f",
                                    fontSize: "13px",
                                  }}
                                  title="Remove role"
                                >
                                  X
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Company Logo
                          {isEditMode && (
                            <small className="text-muted">
                              {" "}
                              (Leave empty to keep current logo)
                            </small>
                          )}
                        </Form.Label>
                        <Form.Control
                          type="file"
                          name="company_logo"
                          onChange={handleFileChange}
                          accept="image/*"
                          required={!isEditMode}
                        />
                        <Form.Text
                          className="text-muted"
                          style={{ fontSize: "13px" }}
                        >
                          Please upload a company logo (PNG, JPG, JPEG)
                        </Form.Text>

                        {/* Show current logo in edit mode */}
                        {isEditMode && currentLogo && !logoPreview && (
                          <div className="mt-2 text-center">
                            <p className="small text-muted">Current Logo:</p>
                            <img
                              src={`${BASE_URL}/uploads/company_logos/${currentLogo}`}
                              alt="Current Company Logo"
                              style={{
                                maxWidth: "200px",
                                maxHeight: "100px",
                                objectFit: "contain",
                                border: "1px solid #dee2e6",
                                borderRadius: "4px",
                                padding: "5px",
                              }}
                            />
                          </div>
                        )}

                        {/* Show new logo preview */}
                        {logoPreview && (
                          <div className="mt-2 text-center">
                            <p className="small text-muted">
                              {isEditMode
                                ? "New Logo Preview:"
                                : "Logo Preview:"}
                            </p>
                            <img
                              src={logoPreview}
                              alt="Company Logo Preview"
                              style={{
                                maxWidth: "200px",
                                maxHeight: "100px",
                                objectFit: "contain",
                                border: "1px solid #dee2e6",
                                borderRadius: "4px",
                                padding: "5px",
                                fontSize: "13px",
                              }}
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label> Select Business Categories</Form.Label>
                        <Select
                          name="primary_category_ids"
                          value={primaryCategories.filter((c) =>
                            formData.primary_category_ids.includes(c.value),
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              primary_category_ids: selected
                                ? selected.map((s) => s.value)
                                : [],
                            }))
                          }
                          options={primaryCategories}
                          isSearchable={true}
                          isMulti={true}
                          placeholder="Select Business Categories"
                          style={{ fontSize: "13px" }}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          closeMenuOnSelect={false}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Feature toggles for variants */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Product Variants</Form.Label>
                        <div
                          style={{
                            display: "flex",
                            gap: "30px",
                            flexWrap: "wrap",
                          }}
                        >
                          <Form.Check
                            type="switch"
                            id="enable_colors"
                            label="Enable Colors"
                            checked={!!formData.enable_colors}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                enable_colors: e.target.checked,
                              }))
                            }
                          />
                          <Form.Check
                            type="switch"
                            id="enable_sizes"
                            label="Enable Sizes"
                            checked={!!formData.enable_sizes}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                enable_sizes: e.target.checked,
                              }))
                            }
                          />
                          <Form.Check
                            type="switch"
                            id="enable_fits"
                            label="Enable Fits"
                            checked={!!formData.enable_fits}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                enable_fits: e.target.checked,
                              }))
                            }
                          />
                          <Form.Check
                            type="switch"
                            id="enable_materials"
                            label="Enable Materials"
                            checked={!!formData.enable_materials}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                enable_materials: e.target.checked,
                              }))
                            }
                          />
                        </div>
                        <small className="text-danger">
                          *Keep at least one variant enabled
                        </small>
                      </Form.Group>
                    </Col>
                    <Button
                      type="submit"
                      className="register-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          {isEditMode
                            ? "Updating Company..."
                            : "Creating Company..."}
                        </>
                      ) : isEditMode ? (
                        "Update Company"
                      ) : (
                        "Create Company"
                      )}
                    </Button>
                  </Row>
                </Form>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
